import React, { useState, useEffect, useMemo, useRef } from "react";
import DataTable from "react-data-table-component";
import apiClient from "../api/apiClient";
import Select from "react-select";

const PlanifLines = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [entDate, setEntDate] = useState("");
  const [project, setproject] = useState(null);
  const [projects, setProjects] = useState([]);
  const [choferes, setChoferes] = useState([]);
  const [camiones, setCamiones] = useState([]);
  const [chofer, setChofer] = useState([]);
  const [camion, setCamion] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [start_route, setStartRoute] = useState("");
  const [end_route, setEndRoute] = useState("");
  const sapUser = JSON.parse(localStorage.getItem("sapUser"));
    const Company = sapUser?.CompanyDB || "";

  // ---- Cargar proyectos una sola vez
  useEffect(() => {
    const fetchProjects = async () => {
      try {
        const res = await apiClient.get("/sales-orders/projects");
        const data = Array.isArray(res.data) ? res.data : [];
        setProjects(
          data.map((p) => ({ value: p.PrjCode, label: `${p.PrjName}` }))
        );
      } catch (e) {
        console.error(e);
        setError("Error al obtener los datos iniciales.");
      }
    };
    fetchProjects();
  }, []);

  // ---- Obtener consolidado
  const fetchInsumos = async () => {
    setLoading(true);
    setError("");
    
    try {
      const { data } = await apiClient.get("/logistica/remisiones/descargas", {
        params: {
          project: project?.value ?? "",
          start_route,
          end_route,
          entDate,
          company: Company,
        },
      });

      const arr = Array.isArray(data) ? data : [];
    // cada elemento del array ya representa UNA entrega (DeliveryNote)
    const mapped = arr.map((any) => ({
        DocEntry: any.DocEntry,
        DocNum: any.DocNum,
        Project: any.Project,
        Gobernacion: any.CardName,
        Institucion: any.Name,
        FechaEntrega: any.DocDueDate || entDate || "",
        RutaIni: start_route,
        RutaFin: end_route,
        RE: any.RE,
        U_CDC: any.U_CDC,
        Orders: arr, // <- todas las líneas de TODAS las OV
      }));
    setOrders(mapped);
    return mapped;
    } catch (e) {
      console.error(e);
      setError("Error al obtener entregas.");
      return [];
    } finally {
      setLoading(false);
    }
  };

  // Helper: detectar entregas sin CDC (no enviadas a SIFEN)
  const findMissingCDC = (rows) =>
    (rows || []).filter((r) => !r.U_CDC || String(r.U_CDC).trim() === "");

    const filteredOrders = useMemo(() => {
        if (!searchTerm) return orders;
        const q = searchTerm.toLowerCase();
        return orders.filter(
        (ov) =>
            (ov.Gobernacion || "").toLowerCase().includes(q) ||
            (ov.Project || "").toLowerCase().includes(q)
        );
    }, [orders, searchTerm]);

    // Agregá este helper (descarga 1 archivo por nombre)
    const downloadOne = async (fileName) => {
        const name = (fileName || "").trim();
        const nameWithExt = name.toLowerCase().endsWith(".pdf") ? name : `${name}.pdf`;

        const r = await apiClient.get("/logistica/remisiones/archivo", {
            params: { name: nameWithExt },
            responseType: "blob",
        });

        const blob = new Blob([r.data], { type: "application/pdf" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = nameWithExt;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
        };

        const handleDownloadAll = async () => {
        if (!filteredOrders.length) return alert("No hay PDFs para descargar.");

        setSubmitting(true);
        try {
            // Conjunto de filas a descargar (puede ser reemplazado tras un refetch)
            let rows = filteredOrders;

            // 1) Verificar si existen entregas sin CDC (no enviadas a SIFEN aún)
            let missing = findMissingCDC(rows);
            if (missing.length > 0) {
              const docNums = missing.map((m) => m.DocNum).join(", ");
              const ok = window.confirm(
                `Existen ${missing.length} entrega(s) sin Remisión Electrónica (CDC):\n` +
                `${docNums}\n\n` +
                `Se reenviarán a SIFEN y se esperarán 10 segundos para volver a verificar. ¿Continuar?`
              );
              if (!ok) {
                setSubmitting(false);
                return;
              }

              // 2) PATCH a Service Layer para reenviar a SIFEN
              try {
                await apiClient.post("/logistica/remisiones/reenviar-sifen", {
                  DocEntries: missing.map((m) => m.DocEntry),
                });
              } catch (e) {
                console.error(e);
                alert("Error al reenviar entregas a SIFEN.");
                setSubmitting(false);
                return;
              }

              // 3) Esperar 10 segundos y volver a listar para verificar U_CDC
              await new Promise((r) => setTimeout(r, 10000));
              const refreshed = await fetchInsumos();
              rows = refreshed;
              missing = findMissingCDC(rows);
              if (missing.length > 0) {
                const docNums2 = missing.map((m) => m.DocNum).join(", ");
                alert(
                  `Aún quedan ${missing.length} entrega(s) sin CDC tras el reenvío:\n${docNums2}\n` +
                  `No se descargarán los PDFs. Intente nuevamente en unos instantes.`
                );
                setSubmitting(false);
                return;
              }
            }

            // 4) Descargar todos los PDFs
            const names = (rows || []).map((r) => r.RE).filter(Boolean);
            if (!names.length) {
              alert("No hay PDFs para descargar.");
              return;
            }
            for (const n of names) {
              await downloadOne(n);
              await new Promise((r) => setTimeout(r, 250));
            }
        } finally {
            setSubmitting(false);
        }
    };


  const columns = [
    { name: "Nro Documento", selector: (row) => row.DocNum, sortable: true, width: "130px" },
    { name: "Proyecto", selector: (row) => row.Project, sortable: true, width: "180px" },
    { name: "Gobernación", selector: (row) => row.Gobernacion, sortable: true, width: "340px" },
    { name: "Institucion", selector: (row) => row.Institucion, sortable: true, width: "300px" },
    { name: "Nombre PDF", selector: (row) => row.RE, sortable: true, width: "360px" },
  ];

  return (
    <div className="container mt-4">
      <h2>Remisiones Electrónicas - Insumos</h2>

      {error && <div className="alert alert-danger">{error}</div>}

      <div className="row">
        <div className="col-md-5">
          <label>Código de Proyecto:</label>
          <Select options={projects} value={project} onChange={setproject} placeholder="Seleccione un proyecto..." isSearchable />
        </div>

        <div className="col-md-2">
          <label>Fecha de Entrega:</label>
          <input type="date" className="form-control" value={entDate} onChange={(e) => setEntDate(e.target.value)} />
        </div>

        <div className="col-md-1">
          <label>Ruta:</label>
          <input type="number" className="form-control" value={start_route} onChange={(e) => setStartRoute(e.target.value)} />
        </div>
      </div>

      <div className="d-flex gap-2 mt-3 flex-wrap">
        <button className="btn btn-success" onClick={fetchInsumos} disabled={loading}>
          {loading ? "Cargando..." : "Obtener Datos"}
        </button>

        <button
        className="btn btn-dark"
        onClick={handleDownloadAll}
        disabled={!filteredOrders.length || submitting}
        >
            {submitting ? "Descargando archivos..." : "Descargar PDFs"}
        </button>
      </div>
      

      <DataTable
        className="mt-3"
        columns={columns}
        data={filteredOrders}
        pagination
        highlightOnHover
        dense
      />
    </div>
  );
};

export default PlanifLines;
